import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SkipSelf,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormArray,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Subject, combineLatest } from 'rxjs';
import { filter, finalize, take, takeUntil, startWith } from 'rxjs/operators';
import { changeDetection } from 'src/app/shared/change-detection';
import { Arrangement } from 'src/app/shared/classes/arrangement';
import { Contact } from 'src/app/shared/classes/contact';
import { ModalService } from 'src/app/shared/services/modal.service';
import { PanelType, SiteService } from 'src/app/shared/services/site.service';
import { UserService } from 'src/app/shared/services/user.service';
import {
  FormValidation,
  ValidationWithValidators,
  ValidationWithErrors,
} from '../../form-validation-alert';
import { Dropdown } from 'primeng/dropdown';
import { RelationToDeceased } from 'src/app/shared/models/arrangement';
import { FormGenerator } from '../../form-generator';
import { FormContactStructure } from '../../form-structure';

export interface ContactValidationIssue {
  contactId: string | null;
  validationId: string;
  path: string | null;
  label: string;
  message: string;
}

@Component({
  selector: 'app-contact-v2',
  templateUrl: './contact-v2.component.html',
  styleUrls: ['./contact-v2.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (container: ControlContainer) => container,
      deps: [[new SkipSelf(), ControlContainer]],
    },
  ],
})
export class ContactV2Component implements OnInit, AfterViewInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  @ViewChildren('contactDropDown') contactDropDown!: QueryList<Dropdown>;

  @Input() controlName!: string;
  @Input() showFooter: boolean;
  @Input() noArrangement: boolean;
  @Input() hideContactSelector: boolean;
  @Input() validators!: ValidationWithValidators[];
  @Input() relationToDeceased!: RelationToDeceased[];
  @Input() relationToDeceasedIsUnique!: boolean;
  @Input() relationToDeceasedPromptOnly!: boolean;
  @Input() contactStates!: { [key: string]: boolean | object };

  @Output() onChange = new EventEmitter<any>();

  formGroup!: FormGroup;
  formRoot!: AbstractControl;
  arrangement!: Arrangement;
  isVisible: boolean = true;
  formValidatorAlert: FormValidation | null;
  validationErrors: any;
  activeContact?: Contact;
  promptedRelationshipStatus: boolean[];
  saving: boolean;

  constructor(
    private siteService: SiteService,
    private http: HttpClient,
    private controlContainer: ControlContainer,
    private modalService: ModalService,
    private userService: UserService,
    private messageService: MessageService
  ) {
    this.showFooter = false;
    this.noArrangement = false;
    this.hideContactSelector = false;
    this.promptedRelationshipStatus = [];
    this.saving = false;
    this.formValidatorAlert = null;

    this.siteService.arrangementContactPanel$.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data) => {
        if (!data.isOpen) {
          // Only if we have a validator should we check for validations errors.
          // Note: We do this because the subscription is executed onload, and it's too late for me to troubleshoot why
          if (this.formValidatorAlert) {
            this.validationErrors = this.formValidatorAlert.validationErrors();
          }
        }
      },
    });
  }

  ngOnInit(): void {
    const control = this.controlName
      ? this.controlContainer.control?.get(this.controlName)
      : this.controlContainer.control;

    if (control && !this.formGroup) {
      this.formGroup = control as FormGroup;
    }

    // Get the root form group (This is the top most formGroup)
    this.formRoot = (this.controlContainer.control as FormGroup).root;

    if (this.formRoot) {
      this.arrangement = this.formRoot.get('_arrangement')?.value as Arrangement;
    }

    // Single combined stream for contacts + selected id
    const idControl = this.formGroup.get('id') as FormControl | null;

    if (!idControl) {
      throw new Error('ContactV2Component: Expected form control "id" to exist.');
    }

    const id$ = idControl.valueChanges.pipe(startWith(idControl.value));

    combineLatest([this.arrangement.contacts$, id$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([contacts, _]) => {
        // Always read the current control value in case it was patched with emitEvent: false
        const currentId = (this.formGroup.get('id') as FormControl).value as string | null;

        const activeChanged = this.updateActiveContact(currentId, contacts);

        // Only auto-apply relations when we actually changed the active contact selection.
        if (activeChanged && currentId) {
          changeDetection(() => {
            const contact = this.activeContact;
            if (contact) {
              this.applyRelationsToSelected(contact, contacts);
              this.generatePromptedRelationshipArray();
            }
          });
        }
      });

    // // Also react to contacts list changes even if id is currently null (e.g. after deletion)
    // this.arrangement.contacts$.pipe(takeUntil(this.unsubscribe$)).subscribe((contacts) => {
    //   const currentId = this.formGroup.get('id')?.value;
    //   if (currentId) {
    //     this.updateActiveContact(currentId, contacts);
    //   }
    // });
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.formValidatorAlert?.clearExistingValidators();
    this.formValidatorAlert = null;

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onAddNewContact(event: Event): void {
    this.contactDropDown.map((item) => item.hide());

    this.siteService.openPanel(PanelType.ArrangementContact, {
      validators: this.validators,
      arrangement: this.arrangement,
      contactStates: this.contactStates,
      onCloseFn: (closeType: string, contactId: string) => this.onClose(closeType, contactId),
    });
  }

  onEditContact(event: Event): void {
    this.siteService.openPanel(PanelType.ArrangementContact, {
      validators: this.validators,
      arrangement: this.arrangement,
      contactStates: this.contactStates,
      contactId: this.formGroup.get('id')?.value,
      contact: this.activeContact,
      onCloseFn: (closeType: string, contactId: string) => this.onClose(closeType, contactId),
    });
  }

  onViewContact(event: Event): void {
    this.isVisible = !this.isVisible;
  }

  onValueClear(): void {
    this.onChange.emit();
  }

  onValueChange(): void {
    this.onChange.emit();
  }

  onTogglePromptedRelationship(checked: boolean, index: number): void {
    if (checked === undefined) {
      return;
    }

    // changeDetection(() => {
    // this.saving = true;

    if (this.activeContact) {
      const relationship = this.activeContact.relationshipOptions.find(
        (r) => r.value === this.relationToDeceased[index]
      );

      if (relationship) {
        if (checked) {
          this.activeContact.addRelationshipToDeceased(relationship);
          this.onChange.emit();

          this.validationErrors = this.formValidatorAlert?.validationErrors();
          this.generatePromptedRelationshipArray();

          this.saveContact();
        } else {
          this.activeContact.removeRelationshipToDeceased(relationship.value);
          this.onChange.emit();
          this.generatePromptedRelationshipArray();

          if (this.relationToDeceased.length) {
            let hasAtLeastOneRelationship = false;

            for (const relationshipToDeceased of this.relationToDeceased) {
              if (this.activeContact.hasRelationToDeceased(relationshipToDeceased)) {
                hasAtLeastOneRelationship = true;
                break;
              }
            }
            if (!hasAtLeastOneRelationship) {
              const modal = this.modalService.generic({
                title: 'Removing Required Relationship',
                copy: [
                  `Removing the <strong>${relationship.name}</strong> relationship will automatically clear the selected contact from this input.`,
                  `Are you sure you want to do that?`,
                ],
                buttons: [
                  { label: `Cancel`, key: 'cancel', class: 'p-button-secondary' },
                  { label: `Yes, I'm sure`, key: 'yes', class: '' },
                ],
              });

              modal.onClose.subscribe({
                next: (key: string) => {
                  if (!this.activeContact) {
                    return;
                  }

                  if (key === 'yes') {
                    this.validationErrors = this.formValidatorAlert?.validationErrors();
                    changeDetection(() => {
                      this.resetState();
                      this.onChange.emit();
                      this.saveContact();
                    });
                  } else {
                    this.activeContact.addRelationshipToDeceased(relationship);
                    this.generatePromptedRelationshipArray();
                    // Re-adding relation: refresh derived lists back to previous state
                    this.onChange.emit();
                  }
                },
              });
            } else {
              // At least one required relation remains; still persist the change
              this.validationErrors = this.formValidatorAlert?.validationErrors();
              this.saveContact();
            }
          } else {
            this.validationErrors = this.formValidatorAlert?.validationErrors();
            this.saveContact();
          }
        }
      }
    }

    // });
  }

  private generatePromptedRelationshipArray(): void {
    this.promptedRelationshipStatus = [];

    if (Array.isArray(this.relationToDeceased) && this.relationToDeceased.length) {
      const relControl = this.activeContact?.form?.get('relationToDeceased');
      const currentValues = new Set(
        Array.isArray(relControl?.value) ? relControl!.value.map((r: any) => r?.value) : []
      );

      for (const relation of this.relationToDeceased) {
        const hasRelationship = currentValues.has(relation);
        this.promptedRelationshipStatus.push(hasRelationship);
      }
    }
    this.validationErrors = this.formValidatorAlert
      ? this.formValidatorAlert.validationErrors()
      : null;
  }

  trackByRelation = (index: number): any =>
    Array.isArray(this.relationToDeceased) ? this.relationToDeceased[index] : index;

  private saveContact(): void {
    this.saving = true;
    changeDetection(() => {
      this.arrangement
        .save()
        .pipe(finalize(() => (this.saving = false)))
        .subscribe({
          next: () => {},
          error: (err) => this.modalService.error(err),
        });
    });
  }

  private onClose(closeType: string, contactId: string): void {
    if (closeType === 'save-on-close' && contactId) {
      this.onChange.emit();

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Contact Saved',
      });

      // Wait for contacts$ to emit the new contact
      this.arrangement.contacts$
        .pipe(
          filter((contacts) => contacts.some((c) => c.id === contactId)),
          take(1)
        )
        .subscribe((contacts) => {
          this.formGroup.get('id')?.patchValue(contactId);
          this.generatePromptedRelationshipArray();
        });
    }
  }

  private updateActiveContact(selectedId: string | null, contacts: Contact[]): boolean {
    const normaliseId = (val: any) => (val !== null && val !== undefined ? val.toString() : val);
    const normalisedSelectedId = normaliseId(selectedId);

    if (!contacts || !contacts.length) {
      return false; // wait until contacts load
    }

    // If ID has been cleared
    if (selectedId) {
      const contact = contacts.find((c) => normaliseId(c.id) === normalisedSelectedId);

      if (contact) {
        if (!this.activeContact || this.activeContact.id !== contact.id) {
          this.setNewActiveContact(contact);
          return true;
        } else {
          // Keep reference fresh in case the contact object was replaced
          this.activeContact = contact;
        }
      } else {
        // Selected id no longer matches any contact; clear state
        this.resetState();
      }
    } else {
      if (this.activeContact) {
        this.removeProvidedRelationship();
      }
      this.resetState();
    }
    this.generatePromptedRelationshipArray();

    return false;
  }

  // Apply relation-to-deceased inputs to the currently selected contact
  private applyRelationsToSelected(contact: Contact, contacts: Contact[]): void {
    if (!Array.isArray(this.relationToDeceased) || this.relationToDeceasedPromptOnly) {
      return;
    }
    for (const r of this.relationToDeceased) {
      if (this.relationToDeceasedIsUnique && Array.isArray(contacts)) {
        contacts
          .filter((c) => c && c.id !== contact.id)
          .forEach((c) => c.removeRelationshipToDeceased(r));
      }
      const relationValue = contact.relationshipOptions?.find((v: any) => v.value === r);
      if (relationValue) {
        contact.addRelationshipToDeceased(relationValue); // Contact class guards duplicates
      }
    }
  }

  private removeProvidedRelationship(): void {
    // Remove previously auto-applied relationships from the old contact
    if (Array.isArray(this.relationToDeceased) && !this.relationToDeceasedPromptOnly) {
      this.relationToDeceased.forEach((r) => {
        try {
          this.activeContact?.removeRelationshipToDeceased(r);
        } catch (e) {
          // Silent fail; defensive safeguard only
        }
      });
    }
  }

  private setNewActiveContact(contact: Contact): void {
    this.formValidatorAlert = new FormValidation(contact.form, this.modalService);
    this.formValidatorAlert.setIgnoreOrphanedValidators(true);
    this.formValidatorAlert.addValidators(this.validators);
    this.validationErrors = this.formValidatorAlert.validationErrors();
    this.activeContact = contact;
  }

  private resetState(): void {
    this.activeContact = undefined;
    this.validationErrors = null;
    this.formValidatorAlert = null;

    // If we have a id, we should clear it
    if (this.formGroup.get('id')?.value) {
      this.formGroup.get('id')?.patchValue(null, { emitEvent: false });
    }
  }

  getValidationIssues(): ContactValidationIssue[] {
    if (!this.formValidatorAlert) {
      return [];
    }

    // Recompute issues to keep cache in sync with latest validators/errors
    this.formValidatorAlert.processMissingValidators();
    const validationIssues: ValidationWithErrors[] =
      this.formValidatorAlert.collectValidationIssues();

    const contactId = this.activeContact?.id ?? null;
    const validationId = this.formValidatorAlert.id;
    const labelToPath = new Map<string, string>();
    (this.validators ?? []).forEach((validator) => {
      labelToPath.set(validator.label, validator.path);
    });

    const contactName = this.getContactDisplayName();
    const issues = validationIssues.map((issue) => {
      const path = issue.path ?? labelToPath.get(issue.label) ?? null;
      const label = issue.label;
      const finalLabel =
        contactName && !label.includes(`(${contactName})`) ? `${label} (${contactName})` : label;
      const message = `<strong>${finalLabel}</strong>: ${issue.errors.join(', ')}`;

      return {
        contactId,
        validationId,
        path,
        label,
        message,
      };
    });

    return issues;
  }

  static collectUniqueIssues(components: ContactV2Component[]): string[] {
    const dedupedIssues = new Map<string, { issue: ContactValidationIssue; index: number }>();
    let order = 0;

    components.forEach((component) => {
      component.getValidationIssues().forEach((issue) => {
        const contactKey = issue.contactId ?? '__no_contact__';
        const pathKey = issue.path ?? issue.label;
        const key = `${contactKey}::${pathKey}`;

        if (!dedupedIssues.has(key)) {
          dedupedIssues.set(key, { issue, index: order++ });
        }
      });
    });

    return Array.from(dedupedIssues.values())
      .sort((a, b) => a.index - b.index)
      .map((entry) => entry.issue.message);
  }

  onSend(event: Event | null, email: string): void {
    this.siteService.closePanel(PanelType.GroupedPanel);

    this.siteService.openPanel(PanelType.SendEmail, {
      email: email,
      showSearch: true,
      arrangement: this.arrangement,
      user: this.userService.userSourceValue,
      formData: {},
    });
  }

  async onCopyToClipboard(event: Event | null, text: string): Promise<void> {
    if (!navigator.clipboard) {
      this.modalService.generic({
        title: 'Error',
        copy: ['Your browser does not support copying to clipboard.'],
        buttons: [{ label: 'Close', key: 'close', class: '' }],
      });

      return;
    }

    try {
      await navigator.clipboard.writeText(text);

      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Copied to clipboard',
      });
    } catch (e) {
      this.modalService.generic({
        title: 'Error',
        copy: ['Your browser does not support copying to clipboard.'],
        buttons: [{ label: 'Close', key: 'close', class: '' }],
      });
    }
  }

  getValidationErrorsWithContactContext(): string[] {
    const errors = this.validationErrors?.list ?? [];
    if (!errors.length) {
      return errors;
    }

    const contactName = this.getContactDisplayName();
    if (!contactName) {
      return errors;
    }

    return errors.map((error: any) => this.appendContactNameToLabel(error, contactName));
  }

  private getContactDisplayName(): string | null {
    const nameGroup = this.formGroup?.get('name') as FormGroup | null;
    const firstName = nameGroup?.get('first')?.value?.trim?.();
    const lastName = nameGroup?.get('last')?.value?.trim?.();

    const combined = [firstName, lastName].filter((segment) => !!segment).join(' ');

    if (combined) {
      return combined;
    }

    const fallbackName = this.activeContact?.name?.trim?.();

    if (fallbackName && fallbackName !== 'Unnamed Contact') {
      return fallbackName;
    }

    return null;
  }

  private appendContactNameToLabel(error: string, contactName: string): string {
    return error.replace(/<strong>(.*?)<\/strong>/, (_match, label: string) => {
      if (!label || label.includes(contactName)) {
        return `<strong>${label}</strong>`;
      }

      return `<strong>${label} (${contactName})</strong>`;
    });
  }
}
